import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { TasksShortQuery$variables } from '../graphql/__generated__/TasksShortQuery.graphql';
import { TaskListShortFragment } from '../graphql/fragments/TaskListShortFragment';
import { TaskShortConnectionFragment } from '../graphql/fragments/TaskShortConnectionFragment';
import { TaskShortFragment } from '../graphql/fragments/TaskShortFragment';
import { TasksShortQuery } from '../graphql/TasksShortQuery';
import useAccess from '../hooks/useAccess';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { SmxPermissionContext } from '../providers/permissionProvider';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterGantLoader } from '../store/slices/gantLoaderSlice';
import DataLoader from '../table/DataLoader';
import { ItemTaskType } from '../types';

interface IGantLoadTask {}

const GantLoadTask: React.FC<IGantLoadTask> = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const { userId } = useContext(SmxPermissionContext);
    const updateTaskFetchKey = useSelector<RootState, number>((state) => state.setting.updateTaskFetchKey);

    const filterFlexibleCommon = [];

    filterProject?.length && filterFlexibleCommon.push(`{"or":[{"project_id":"${filterProject.join('|')}"}]}`);

    const accessAll = useAccess(['schedule-task.all.view']);

    if (!accessAll) {
        const accessOwner = useAccess(['schedule-task.owner']);
        const accessAssignee = useAccess(['schedule-task.assignee']);

        const filterTaskOwnerAssignee = [];
        accessOwner && filterTaskOwnerAssignee.push(`{"owner_task_id":"${userId}"}`);
        accessAssignee && filterTaskOwnerAssignee.push(`{"assignee_for_task":"*${userId}"}`);

        (accessAssignee || accessOwner) && filterFlexibleCommon.push(`{"or":[${filterTaskOwnerAssignee.join(',')}]}`);
    }

    const filterFlexible = filterFlexibleCommon?.length ? `[${filterFlexibleCommon.join(',')}]` : undefined;

    return (
        <DataLoader<TasksShortQuery$variables, {}>
            query={TasksShortQuery}
            fragmentList={TaskListShortFragment}
            fragment={TaskShortFragment}
            fragmentConnection={TaskShortConnectionFragment}
            nameQuery={'tasks'}
            onUpdateData={(rows: any[], count: number, connectionId: string) => {
                const newMappingTaskByProject: { [key: string]: ItemTaskType[] } = {};

                rows.forEach((task: ItemTaskType) => {
                    if (task.schedule_item_id) {
                        if (!newMappingTaskByProject[task.schedule_item_id]) {
                            newMappingTaskByProject[task.schedule_item_id] = [];
                        }
                        newMappingTaskByProject[task.schedule_item_id] = [
                            ...newMappingTaskByProject[task.schedule_item_id],
                            {
                                ...task,
                                assignee_for_task: task.assignee_for_task
                                    ? (task.assignee_for_task as string).split(',')
                                    : [],
                            },
                        ];
                    }
                });

                dispatch(
                    setterGantLoader({
                        dataTaskFromServer: { rows, count, connectionId },
                        mappingTaskByProject: newMappingTaskByProject,
                    }),
                );

                dispatch(
                    setterCalendar({
                        connectionTaskId: connectionId,
                    }),
                );
            }}
            varRequest={{
                filter_flexible: filterFlexible,
            }}
            externalFetchKey={updateTaskFetchKey}
        />
    );
};

export default GantLoadTask;
